import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ApiContext } from "../../../context/MyContext";
import { useUtils } from "../../../context/UtilsContext";
import { fetchEmployees } from "../../../service/employeeService";
import { employeeListCache } from "../../../utils/listCache/caches";
import { resolveBookingEventTypeId } from "../../../pages/comms/Bookings/bookingEventType";
import { fetchEmployeeLeaves, modifyEmployeeLeaves } from "../core/leaveApi";
import {
  LEAVE_STATUS,
  leaveStatusIdFor,
  leaveStatusToken,
  leaveTypeOptions,
} from "../core/leaveLookups";
import { currentManagerNames } from "../core/managerCheck";
import { activeBookingsInLeave, loadBookings } from "../core/leaveBookings";
import { findOverlappingLeave, friendlyLeaveError, overlapMessage } from "../core/leaveErrors";

// `/fetch-employees` is the same tenant-wide payload for every caller; this is
// the key the Bookings roster already caches it under.
const ALL_EMPLOYEES_KEY = "all";

/**
 * The signed-in coach's own leaves: list, apply, withdraw, plus the
 * manager-assigned gate the Apply button needs.
 *
 * @param {{ enabled: boolean }} options - Off for anyone the Leave tab isn't
 *   shown to, so no request is made on their behalf.
 * @returns {{
 *   employeeId: string|null,
 *   leaves: Array<Object>, loading: boolean, error: string|null, reload: Function,
 *   pendingCount: number,
 *   leaveTypes: Array<{ id: string, label: string, hint: string }>,
 *   manager: { status: 'loading'|'ok'|'none'|'error', names: Array<string> },
 *   countBookings: (window: { start: string, end: string }) => Promise<number>,
 *   overlapFor: (window: { start: string, end: string }) => string|null,
 *   apply: (fields: Object) => Promise<void>,
 *   withdraw: (leave: Object) => Promise<void>,
 * }}
 */
export function useMyLeaves({ enabled }) {
  const { get, post, employeeData } = useContext(ApiContext) || {};
  const { sessionUtils, corporateUtils, fetchCorporateUtils } = useUtils() || {};
  const employeeId = employeeData?.data?.employee_id || null;
  const eventTypeId = resolveBookingEventTypeId(sessionUtils);
  const active = Boolean(enabled && employeeId && eventTypeId);

  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(active);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [manager, setManager] = useState({ status: "loading", names: [] });
  const [lookupsAttempted, setLookupsAttempted] = useState(false);

  // Leave types/statuses ride on corporateUtils. A session copy saved before
  // the leave lookups existed lacks them, so that one is refetched once.
  const hasLookups = Array.isArray(corporateUtils?.leave_types);
  useEffect(() => {
    if (!enabled || hasLookups || lookupsAttempted) return;
    if (typeof fetchCorporateUtils !== "function") {
      setLookupsAttempted(true);
      return;
    }
    Promise.resolve(fetchCorporateUtils({ force: Boolean(corporateUtils) }))
      .catch(() => null)
      .finally(() => setLookupsAttempted(true));
  }, [enabled, hasLookups, lookupsAttempted, fetchCorporateUtils]);

  useEffect(() => {
    if (!active) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchEmployeeLeaves(get, { eventTypeId, employeeId })
      .then((rows) => {
        if (!cancelled) setLeaves(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(friendlyLeaveError(err, { action: "load" }));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, employeeId, eventTypeId, reloadToken]);

  useEffect(() => {
    if (!enabled || !employeeId) return undefined;
    let cancelled = false;
    setManager({ status: "loading", names: [] });
    (async () => {
      try {
        let employees = employeeListCache.get(ALL_EMPLOYEES_KEY);
        if (!employees) {
          const payload = await fetchEmployees(get);
          employees = Array.isArray(payload?.employees) ? payload.employees : [];
          employeeListCache.set(ALL_EMPLOYEES_KEY, employees);
        }
        const names = currentManagerNames(employees, employeeId);
        if (!cancelled) setManager({ status: names.length ? "ok" : "none", names });
      } catch {
        if (!cancelled) setManager({ status: "error", names: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, employeeId]);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  const countBookings = useCallback(
    async ({ start, end }) => {
      if (!eventTypeId || !employeeId) return 0;
      const records = await loadBookings(get, { eventTypeId, from: start, to: end });
      return activeBookingsInLeave(records, { employee_id: employeeId, start_date: start, end_date: end })
        .length;
    },
    [get, eventTypeId, employeeId]
  );

  // The coach's own pending/approved leave these dates would clash with, as a
  // sentence — checked before sending, so the form can say so up front
  // instead of waiting for the API's 409.
  const overlapFor = useCallback(
    (range) => {
      const clash = findOverlappingLeave(leaves, range);
      return clash ? overlapMessage(clash) : null;
    },
    [leaves]
  );

  const apply = useCallback(
    async ({ leaveTypeId, start, end, reason }) => {
      const clash = overlapFor({ start, end });
      if (clash) throw new Error(clash);
      try {
        await modifyEmployeeLeaves(post, {
          eventTypeId,
          leaves: [
            {
              operation: "ADD",
              employee_id: employeeId,
              leave_type_id: leaveTypeId,
              start_date: start,
              end_date: end,
              ...(reason ? { reason } : {}),
            },
          ],
        });
      } catch (err) {
        throw new Error(friendlyLeaveError(err, { action: "apply", leaves }));
      }
      reload();
    },
    [post, eventTypeId, employeeId, leaves, overlapFor, reload]
  );

  const withdraw = useCallback(
    async (leave) => {
      const statusId = leaveStatusIdFor(corporateUtils, LEAVE_STATUS.CANCELLED);
      if (!statusId) throw new Error("Leave statuses are not loaded yet. Try again.");
      try {
        await modifyEmployeeLeaves(post, {
          eventTypeId,
          leaves: [{ operation: "ADD", id: leave.id, leave_status_id: statusId }],
        });
      } catch (err) {
        throw new Error(friendlyLeaveError(err, { action: "withdraw", leaves }));
      }
      reload();
    },
    [post, eventTypeId, corporateUtils, leaves, reload]
  );

  const pendingCount = useMemo(
    () => leaves.filter((l) => leaveStatusToken(l.status) === LEAVE_STATUS.PENDING).length,
    [leaves]
  );
  const leaveTypes = useMemo(() => leaveTypeOptions(corporateUtils), [corporateUtils]);

  return {
    employeeId,
    leaves,
    loading,
    error: active || !enabled ? error : "Leave isn't available for this account.",
    reload,
    pendingCount,
    leaveTypes,
    manager,
    countBookings,
    overlapFor,
    apply,
    withdraw,
  };
}
